import type { Avatar as AvatarValue } from "@ark-bot/protocol";
import { useEffect, useRef, useState } from "react";

import { api } from "../api.js";
import { ZOOM_MAX, ZOOM_MIN, clampOffset, clampZoom, freed, placement as picturePlacement } from "../avatar-frame.js";
import { useBackClose } from "../back.js";
import { useText } from "../i18n.js";
import { AVATAR } from "../text/avatar.js";

import { ActiveGlide } from "./ActiveGlide.js";
import { AVATAR_COLORS, AVATAR_SHAPES, Avatar, colorFor, initialsOf } from "./Avatar.js";

interface Props {
  readonly id: string;
  readonly name: string;
  readonly value: AvatarValue | undefined;
  /** Where the face is on screen; the popover hangs under it, or sits over it when there is no room below. */
  readonly anchor: DOMRect;
  /** The choice made: a new avatar, or `undefined` for the one derived from the name. */
  readonly onApply: (avatar: AvatarValue | undefined) => void;
  readonly onClose: () => void;
}

const PICKER_WIDTH = 320;
const PICKER_HEIGHT = 330;

/**
 * Fixed to the viewport rather than to the form, so a scrolling pane or the
 * sliding panel cannot clip it: centred under the face, kept on screen, and
 * flipped above the face when the bottom of the window is too close.
 */
function placement(anchor: DOMRect): React.CSSProperties {
  const width = Math.min(PICKER_WIDTH, window.innerWidth - 24);
  const left = Math.max(12, Math.min(anchor.left + anchor.width / 2 - width / 2, window.innerWidth - width - 12));
  const below = window.innerHeight - anchor.bottom >= PICKER_HEIGHT + 12 || anchor.top < PICKER_HEIGHT + 12;
  return below ? { position: "fixed", left, top: anchor.bottom + 6, width } : { position: "fixed", left, bottom: window.innerHeight - anchor.top + 6, width };
}

const IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp", "image/gif"];

/** The frame the picture is cut to in the picker, and the room around it the picture may be moved into. */
const FRAME = 96;
const ROOM = 48;

/**
 * The popover under an agent's face, as the prototype's avatar picker: a
 * tab for a mark (shape, colour, label), one for a picture (dropped, pasted
 * or browsed, then dragged into place and zoomed), and Reset to the mark
 * derived from the name. Nothing is applied until "Set avatar".
 */
export function AvatarPicker({ id, name, value, anchor, onApply, onClose }: Props): React.JSX.Element {
  const t = useText(AVATAR);
  const [tab, setTab] = useState<"mark" | "upload">(value?.kind === "image" ? "upload" : "mark");
  const [draft, setDraft] = useState<AvatarValue | undefined>(value);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [over, setOver] = useState(false);
  const element = useRef<HTMLDivElement>(null);
  const dragging = useRef<{ x: number; y: number; fromX: number; fromY: number } | null>(null);
  const image = draft?.kind === "image" ? draft : null;
  const mark = draft?.kind === "mark" ? draft : null;
  const color = mark?.color ?? colorFor(id);

  // Anything outside the popover closes it, as a menu closes.
  useEffect(() => {
    const away = (event: MouseEvent | TouchEvent): void => {
      if (element.current != null && event.target instanceof Node && element.current.contains(event.target)) return;
      onClose();
    };
    const key = (event: KeyboardEvent): void => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", away);
    document.addEventListener("touchstart", away);
    document.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("touchstart", away);
      document.removeEventListener("keydown", key);
    };
  }, [onClose]);

  const pick = async (file: File | undefined | null): Promise<void> => {
    if (file == null) return;
    if (!IMAGE_TYPES.includes(file.type)) {
      setError(t.wrongType);
      return;
    }
    setUploading(true);
    setError(null);
    try {
      const blob = await api.uploadBlob(file);
      setDraft({ kind: "image", blobId: blob.blobId, mimeType: blob.mimeType as "image/png", frame: "free", zoom: 1, x: 0, y: 0, ...(draft?.shape == null ? {} : { shape: draft.shape }) });
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : String(failure));
    } finally {
      setUploading(false);
    }
  };

  // A picture pasted anywhere while the upload tab is open lands here, as the prototype's drop zone says.
  useEffect(() => {
    if (tab !== "upload") return;
    const paste = (event: ClipboardEvent): void => {
      const file = [...(event.clipboardData?.files ?? [])].find((one) => one.type.startsWith("image/"));
      if (file == null) return;
      event.preventDefault();
      void pick(file);
    };
    document.addEventListener("paste", paste);
    return () => document.removeEventListener("paste", paste);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  /**
   * Dragging moves the picture with the finger, in halves of the frame; it
   * may go partly or wholly outside the frame, into the room around it. An
   * older record is said the new way first, so it carries on from where it is.
   */
  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>): void => {
    if (image == null) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    const free = freed(image);
    if (free !== image) setDraft(free);
    dragging.current = { x: event.clientX, y: event.clientY, fromX: free.x ?? 0, fromY: free.y ?? 0 };
  };
  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>): void => {
    const from = dragging.current;
    if (from == null || image == null) return;
    setDraft({ ...freed(image), x: clampOffset(from.fromX + (event.clientX - from.x) / (FRAME / 2)), y: clampOffset(from.fromY + (event.clientY - from.y) / (FRAME / 2)) });
  };
  /** The picture drawn faint over the whole stage, so what lies outside the frame can be seen and aimed. */
  const ghostStyle = (): React.CSSProperties => {
    if (image == null) return {};
    const at = picturePlacement(freed(image));
    return { left: ROOM + (at.left + at.width / 2) * FRAME, top: ROOM + (at.top + at.width / 2) * FRAME, width: at.width * FRAME };
  };
  /** The outline, for a mark or a picture: the same eight, drawn in the current colour or with the picture itself. */
  const shapeRow = (): React.JSX.Element => (
    <div className="picker-shapes" role="radiogroup" aria-label={t.shape}>
      {AVATAR_SHAPES.map((option) => {
        const current = (draft?.shape ?? "circle") === option.shape && draft != null;
        const preview: AvatarValue = image == null ? { kind: "mark", shape: option.shape, color, ...(mark?.label == null ? {} : { label: mark.label }) } : { ...image, shape: option.shape };
        return (
          <button key={option.shape} type="button" role="radio" aria-checked={current} className="picker-shape" title={option.label(t)} onClick={() => (image == null ? setMark({ shape: option.shape }) : setDraft({ ...image, shape: option.shape }))}>
            <Avatar id={id} name={name} avatar={preview} size={34} />
          </button>
        );
      })}
    </div>
  );
  const endDrag = (): void => {
    dragging.current = null;
  };

  const setMark = (patch: Partial<Extract<AvatarValue, { kind: "mark" }>>): void => {
    const next = { kind: "mark" as const, shape: mark?.shape ?? "circle", color, ...(mark?.label == null ? {} : { label: mark.label }), ...patch };
    if (next.label != null && next.label.length === 0) delete (next as { label?: string }).label;
    setDraft(next);
  };

  const unchanged = JSON.stringify(draft ?? null) === JSON.stringify(value ?? null);

  return (
    <div className="avatar-picker popover" ref={element} role="dialog" aria-label={t.avatar} style={placement(anchor)}>
      <div className="picker-tabs" role="tablist">
        <ActiveGlide selector='[aria-selected="true"]' className="glide-picker" />
        <button type="button" role="tab" aria-selected={tab === "mark"} onClick={() => setTab("mark")}>
          {t.mark}
        </button>
        <button type="button" role="tab" aria-selected={tab === "upload"} onClick={() => setTab("upload")}>
          {t.upload}
        </button>
        <button
          type="button"
          className="picker-reset"
          onClick={() => {
            onApply(undefined);
            onClose();
          }}
          disabled={value == null}
          title={t.resetTitle}
        >
          {t.reset}
        </button>
      </div>

      {tab === "mark" ? (
        <div className="picker-body">
          {shapeRow()}
          <label className="picker-label">
            <span className="muted">{t.label}</span>
            <input value={mark?.label ?? ""} maxLength={8} placeholder={initialsOf(name)} onChange={(event) => setMark({ label: event.target.value })} />
          </label>
          <div className="picker-swatches" role="radiogroup" aria-label={t.colour}>
            {AVATAR_COLORS.map((swatch) => (
              <button key={swatch} type="button" role="radio" aria-checked={mark?.color === swatch} className="swatch" style={{ background: swatch }} aria-label={t.colourOf(swatch)} onClick={() => setMark({ color: swatch })} />
            ))}
          </div>
        </div>
      ) : (
        <div className="picker-body">
          {image == null ? (
            <label
              className={`dropzone${over ? " over" : ""}`}
              onDragOver={(event) => {
                event.preventDefault();
                setOver(true);
              }}
              onDragLeave={() => setOver(false)}
              onDrop={(event) => {
                event.preventDefault();
                setOver(false);
                void pick(event.dataTransfer.files[0]);
              }}
            >
              <span className="muted">{uploading ? t.uploading : t.dropImage}</span>
              <span className="muted">{t.or}</span>
              <span className="dropzone-browse">{t.browse}</span>
              <input type="file" accept={IMAGE_TYPES.join(",")} onChange={(event) => void pick(event.target.files?.[0])} disabled={uploading} />
            </label>
          ) : (
            <div className="picker-image">
              <div className="avatar-stage" style={{ width: FRAME + 2 * ROOM, height: FRAME + 2 * ROOM }} onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={endDrag} onPointerCancel={endDrag} title={t.dragTitle}>
                <img className="avatar-ghost" src={api.blobUrl(image.blobId)} alt="" draggable={false} style={ghostStyle()} />
                <div className="avatar-frame" style={{ left: ROOM, top: ROOM, width: FRAME, height: FRAME }}>
                  <Avatar id={id} name={name} avatar={image} size={FRAME} />
                </div>
              </div>
              {shapeRow()}
              <label className="picker-zoom">
                <span className="muted">{t.zoom}</span>
                <input type="range" min={ZOOM_MIN} max={ZOOM_MAX} step={0.05} value={image.zoom ?? 1} onChange={(event) => setDraft({ ...freed(image), zoom: clampZoom(Number(event.target.value)) })} />
              </label>
              <label className="filebutton">
                {uploading ? t.uploading : t.replace}
                <input type="file" accept={IMAGE_TYPES.join(",")} onChange={(event) => void pick(event.target.files?.[0])} disabled={uploading} />
              </label>
            </div>
          )}
        </div>
      )}
      {error != null ? <div className="settings-error">{error}</div> : null}
      <div className="picker-actions">
        <button type="button" onClick={onClose}>
          {t.cancel}
        </button>
        <button
          type="button"
          className="primary"
          disabled={unchanged || uploading}
          onClick={() => {
            onApply(draft);
            onClose();
          }}
        >
          {t.setAvatar}
        </button>
      </div>
    </div>
  );
}

interface FieldProps {
  readonly id: string;
  readonly name: string;
  readonly value: AvatarValue | undefined;
  readonly onChange: (avatar: AvatarValue | undefined) => void;
  readonly size?: number;
}

/** The face, large and centred, that opens the picker when pressed: the top of every profile form. */
export function AvatarField({ id, name, value, onChange, size = 88 }: FieldProps): React.JSX.Element {
  const t = useText(AVATAR);
  const [anchor, setAnchor] = useState<DOMRect | null>(null);
  const open = anchor != null;
  useBackClose(open, () => setAnchor(null));
  return (
    <div className="avatar-field">
      <button type="button" className="avatar-button" onClick={(event) => setAnchor(open ? null : event.currentTarget.getBoundingClientRect())} aria-haspopup="dialog" aria-expanded={open} title={t.changeAvatar}>
        <Avatar id={id} name={name} avatar={value} size={size} />
        <span className="avatar-edit" aria-hidden="true">
          <svg viewBox="0 0 20 20" width="14" height="14">
            <path d="M4 14.5V16h1.5l8.6-8.6-1.5-1.5L4 14.5ZM14.9 4.4l.7-.7a1 1 0 0 1 1.4 0l.3.3a1 1 0 0 1 0 1.4l-.7.7-1.7-1.7Z" fill="currentColor" />
          </svg>
        </span>
      </button>
      {anchor != null ? <AvatarPicker id={id} name={name} value={value} anchor={anchor} onApply={onChange} onClose={() => setAnchor(null)} /> : null}
    </div>
  );
}
